import Twig from "twig";

import { restoreProviderTags } from "../esp-templating/provider-tag-mask";
import { MAX_TAG_LENGTH } from "../esp-templating/tag-bounds";

// Renders Twig (Xtremepush) templates for the personalization preview with
// twig.js. Templates are customer- and agent-authored and also render on the
// server, so the engine is a private instance hardened against code execution,
// file reads and runaway loops. An output tag the engine cannot evaluate, such
// as Xtremepush's `snippet()`, stays as written while the rest renders.

/** Bounds the `range()` and `..` loops of one render so a template cannot stall it. */
const MAX_RANGE_ITEMS_PER_RENDER = 10_000;

const OUTPUT_TAG = String.raw`\{\{-?(?=[\s\S]{0,${MAX_TAG_LENGTH}}?-?\}\})[\s\S]*?-?\}\}`;
const TWIG_TAG_RE = new RegExp(
  [
    OUTPUT_TAG,
    String.raw`\{%-?(?=[\s\S]{0,${MAX_TAG_LENGTH}}?-?%\})[\s\S]*?-?%\}`,
    String.raw`\{#(?=[\s\S]{0,${MAX_TAG_LENGTH}}?#\})[\s\S]*?#\}`,
  ].join("|"),
  "g",
);
const OUTPUT_TAG_RE = new RegExp(OUTPUT_TAG, "g");
const BLOCK_TAG_NAME_RE = new RegExp(
  String.raw`\{%[-~]?\s*([A-Za-z_]\w*)\b(?=[\s\S]{0,${MAX_TAG_LENGTH}}?[-~]?%\})[\s\S]*?[-~]?%\}`,
  "g",
);

/** Every block tag Twig 3 defines; any other name is a vendor tag such as `{% web_view %}`. */
const TWIG_BLOCK_TAGS = new Set([
  "apply",
  "endapply",
  "autoescape",
  "endautoescape",
  "block",
  "endblock",
  "cache",
  "endcache",
  "deprecated",
  "do",
  "embed",
  "endembed",
  "extends",
  "flush",
  "for",
  "endfor",
  "from",
  "if",
  "elseif",
  "else",
  "endif",
  "import",
  "include",
  "macro",
  "endmacro",
  "sandbox",
  "endsandbox",
  "set",
  "endset",
  "spaceless",
  "endspaceless",
  "use",
  "verbatim",
  "endverbatim",
  "with",
  "endwith",
]);
const STRING_LITERAL_RE = /"[^"]*"|'[^']*'/g;

/** Twig-only tags, matched against the whole tag. */
const TWIG_ONLY_TAGS = [
  /^\{#/,
  /^\{%[-~]?\s*(?:elseif|set|endset|apply|verbatim|do)\b/,
];

/**
 * Twig syntax inside a tag that is invalid in both Liquid and Handlebars,
 * matched against the tag body with its delimiters and whitespace modifiers
 * removed, so Handlebars' `{{~ x ~}}` does not count as concatenation.
 */
const TWIG_ONLY_EXPRESSIONS = [
  /~/,
  /\?/,
  /[A-Za-z_]\w*\(/,
  /\|\s*[A-Za-z_]\w*\s+\(/,
  // `snippet ('x')`: a Handlebars subexpression starts with a helper name instead.
  /(?:^|[^\w#/^>.])(?!(?:in|and|or|not|if|elsif|unless|when|contains)\s)[A-Za-z_]\w*\s+\(\s*(?:''|\d|\)|\[|\{)/,
  /\bin\s+[^\s(]\S*\s*\.\./,
  /\bis\s+(?:not\s+)?(?:defined|empty|null|none|iterable|even|odd)\b/,
];
const HANDLEBARS_COMMENT_RE = /^\{\{~?!/;
const TAG_DELIMITERS_RE = /^\{[{%#][-~]?|[-~]?[}%#]\}$/g;

const RAW_PLACEHOLDER_PREFIX = "__HT_TWIG_RAW_";

type TwigRenderParameters = Twig.Parameters & {
  rethrow: boolean;
  autoescape: boolean;
};

/** The twig.js internals the hardening replaces, which `@types/twig` omits. */
interface TwigInternals {
  Error: new (message: string) => Error;
  Promise: { resolve(value: unknown): unknown };
  expression: {
    resolveAsync: (
      this: unknown,
      value: unknown,
      ...rest: unknown[]
    ) => unknown;
  };
  functions: Record<string, (...args: unknown[]) => unknown>;
  Templates: {
    registerLoader(method: string, loader: () => never): void;
  };
}

/** `factory` builds an isolated instance; `@types/twig` predates it. */
type TwigModule = typeof Twig & { factory(): typeof Twig };

const engine = (Twig as TwigModule).factory();
engine.extend((internals) => hardenTwig(internals as unknown as TwigInternals));

let rangeItemsLeft = MAX_RANGE_ITEMS_PER_RENDER;

/**
 * Whether the template uses Twig syntax that Liquid and Handlebars cannot
 * parse. The backend labels Xtremepush templates LIQUID, so the renderer uses
 * this to pick the Twig engine.
 */
export function containsTwig(template: string): boolean {
  for (const [tag] of template.matchAll(TWIG_TAG_RE)) {
    if (HANDLEBARS_COMMENT_RE.test(tag)) continue;
    const code = tag.replace(STRING_LITERAL_RE, "''");
    if (TWIG_ONLY_TAGS.some((marker) => marker.test(code))) return true;
    // A vendor block tag (Braze `{% abort_message('x') %}`) is not Twig evidence.
    const blockName = /^\{%[-~]?\s*([A-Za-z_]\w*)/.exec(code)?.[1];
    if (blockName !== undefined && !TWIG_BLOCK_TAGS.has(blockName)) continue;
    const body = code.replace(TAG_DELIMITERS_RE, "");
    if (TWIG_ONLY_EXPRESSIONS.some((marker) => marker.test(body))) return true;
  }
  return false;
}

/** Inputs for one `renderTwig` call. */
export interface RenderTwigOptions {
  /** Sample values, with dotted names already nested into objects. */
  scope: Record<string, unknown>;
  /** HTML-escapes every output, as Twig's `html` autoescape strategy does. */
  escapeHtml?: boolean;
}

/** The outcome of one `renderTwig` call. */
export interface RenderTwigResult {
  /** The rendered text, or the template unchanged when `error` is set. */
  output: string;
  /** Why the template could not render at all. */
  error: string | null;
  /** Vendor block tags and output tags left as written. */
  skippedTags: string[];
}

/**
 * Renders a Twig template. Vendor block tags and output tags that fail on
 * their own are left as written. A template that still fails comes back
 * unchanged with its error.
 */
export function renderTwig(
  template: string,
  { scope, escapeHtml = false }: RenderTwigOptions,
): RenderTwigResult {
  try {
    return {
      output: renderOnce(template, scope, escapeHtml),
      error: null,
      skippedTags: [],
    };
  } catch (error) {
    const { maskedTemplate, placeholders } = maskUnsupportedTags(
      template,
      scope,
      escapeHtml,
    );
    if (placeholders.size > 0) {
      try {
        return {
          output: restoreProviderTags(
            renderOnce(maskedTemplate, scope, escapeHtml),
            placeholders,
          ),
          error: null,
          skippedTags: [...placeholders.values()],
        };
      } catch {
        // Fall through and report the original failure.
      }
    }
    return {
      output: template,
      error: error instanceof Error ? error.message : String(error),
      skippedTags: [],
    };
  }
}

function renderOnce(
  template: string,
  scope: Record<string, unknown>,
  escapeHtml: boolean,
): string {
  rangeItemsLeft = MAX_RANGE_ITEMS_PER_RENDER;
  const parameters: TwigRenderParameters = {
    data: template,
    rethrow: true,
    autoescape: escapeHtml,
  };
  return engine.twig(parameters).render(scope);
}

// A vendor block tag or an output tag that fails alone is unsupported wherever
// it sits, so masking only those keeps every tag the engine can evaluate.
function maskUnsupportedTags(
  template: string,
  scope: Record<string, unknown>,
  escapeHtml: boolean,
): { maskedTemplate: string; placeholders: Map<string, string> } {
  let prefix = RAW_PLACEHOLDER_PREFIX;
  while (template.includes(prefix)) prefix = `${prefix}_`;
  const placeholders = new Map<string, string>();
  const mask = (tag: string): string => {
    const placeholder = `${prefix}${placeholders.size}__`;
    placeholders.set(placeholder, tag);
    return placeholder;
  };
  const withoutVendorTags = template.replace(
    BLOCK_TAG_NAME_RE,
    (tag, name: string) => (TWIG_BLOCK_TAGS.has(name) ? tag : mask(tag)),
  );
  const maskedTemplate = withoutVendorTags.replace(OUTPUT_TAG_RE, (tag) => {
    try {
      renderOnce(tag, scope, escapeHtml);
      return tag;
    } catch {
      return mask(tag);
    }
  });
  return { maskedTemplate, placeholders };
}

function unsupported(name: string): () => never {
  return () => {
    throw new Error(`${name} is not supported in the preview`);
  };
}

function hardenTwig(internals: TwigInternals): void {
  // A template reaches a function only through inherited members such as
  // `constructor`; never calling one closes the path to `Function`.
  const resolveAsync = internals.expression.resolveAsync;
  internals.expression.resolveAsync = function (value, ...rest) {
    if (typeof value === "function") {
      return internals.Promise.resolve(undefined);
    }
    return resolveAsync.call(this, value, ...rest);
  };

  internals.functions.attribute = (object, key) => {
    if (object === null || typeof object !== "object") return undefined;
    if (typeof key !== "string" && typeof key !== "number") return undefined;
    if (!Object.hasOwn(object, key)) return undefined;
    const value = (object as Record<string | number, unknown>)[key];
    return typeof value === "function" ? undefined : value;
  };

  const range = internals.functions.range;
  if (range) {
    internals.functions.range = (low, high, step = 1) => {
      const [from, to, by] = [low, high, step].map(Number);
      if (
        from === undefined ||
        to === undefined ||
        by === undefined ||
        !Number.isFinite(from) ||
        !Number.isFinite(to) ||
        !Number.isFinite(by) ||
        by === 0
      ) {
        throw new internals.Error(
          "range() takes finite numbers and a nonzero step",
        );
      }
      const count = Math.floor(Math.abs(to - from) / Math.abs(by)) + 1;
      if (count > rangeItemsLeft) {
        throw new internals.Error("range() is too large for the preview");
      }
      rangeItemsLeft -= count;
      return range(from, to, Math.abs(by));
    };
  }

  // No template may load another template or a file. This stops `include`,
  // `embed`, `extends`, `import`, `from`, `use` and `source()`.
  internals.Templates.registerLoader("fs", unsupported("Loading templates"));
  internals.Templates.registerLoader("ajax", unsupported("Loading templates"));
  internals.functions.source = unsupported("source()");
  internals.functions.template_from_string = unsupported(
    "template_from_string()",
  );
}
